import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import {
  bulkDeleteWebsiteSignups,
  cancelWebsiteSignup,
  deleteWebsiteSignup,
  getWebsiteSignups,
} from '@/lib/api';
import {
  ATTENDANCE_OPTIONS,
  SIGNUP_STATUSES,
  attendanceLabel,
  displayValue,
  formatDate,
  fullName,
  heardAboutLabel,
  signupStatusMeta,
  sourcePageLabel,
} from '@/lib/websiteEngagement';
import {
  BulkDeleteRegistrationsDialog,
  CancelRegistrationDialog,
  DeleteRegistrationDialog,
} from '@/components/admin/RegistrationActions';
import WebsiteEngagementShell from '@/components/admin/WebsiteEngagementShell';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
// The Agencies workspace's table styling, shared rather than re-typed.
import {
  DesktopTableOnly,
  MobileCard,
  MobileCardField,
  MobileCardFields,
  MobileCardHeader,
  MobileCardList,
  MobileCardNotice,
  ROW_DENSITY_CLASSES,
  StatusPill,
  TableEmptyRow,
  TableFilterCard,
  adminInputClass,
  filterPillActiveClass,
  filterPillClass,
  tableCardClass,
  tableCellClass,
  tableHeadCellClass,
  tableHeaderRowClass,
  tableRowClass,
} from '@/components/admin/AdminTableStyles';
import { cn } from '@/lib/utils';
import {
  Ban, CheckCircle2, ChevronLeft, ChevronRight, CircleHelp,
  Eye, Laptop, MapPin, Search, Trash2, UserX, Users,
} from 'lucide-react';

const PAGE_SIZE = 25;
const ALL = 'all'; // sentinel for "no filter" (Radix Select cannot use "")

// Each column has a floor it will not shrink past and a share of whatever space
// is left over. Fixed pixel widths left the grid narrower than its card on a
// wide screen, so the header bar stopped short of the edge — these track the
// card instead, and the floors are what the table scrolls to protect.
//
// The two `fixed` columns are the exceptions: selection and actions keep their
// width and stick to the edges, so the controls stay reachable however far the
// middle of the table has been scrolled.
const COLUMNS = [
  { id: 'select', label: '', min: 44, grow: 0, fixed: true },
  { id: 'name', label: 'Name', min: 150, grow: 1.3 },
  { id: 'email', label: 'Email', min: 180, grow: 1.6 },
  { id: 'phone', label: 'Phone', min: 120, grow: 0.9 },
  { id: 'attendance', label: 'Online/In-Person', min: 135, grow: 0.9 },
  { id: 'heard', label: 'Referral Source', min: 130, grow: 1 },
  { id: 'agency', label: 'Agency', min: 130, grow: 1 },
  { id: 'event', label: 'Event', min: 150, grow: 1.2 },
  { id: 'status', label: 'Status', min: 100, grow: 0.7 },
  { id: 'submitted', label: 'Submitted', min: 110, grow: 0.8 },
  { id: 'actions', label: 'Actions', min: 158, grow: 0, fixed: true },
];

const GRID_TEMPLATE_COLUMNS = COLUMNS
  .map((column) => (column.fixed ? `${column.min}px` : `minmax(${column.min}px, ${column.grow}fr)`))
  .join(' ');
const TABLE_MIN_WIDTH = COLUMNS.reduce((total, column) => total + column.min, 0);
const ROW_PADDING = ROW_DENSITY_CLASSES.comfortable;
const SKELETON_ROWS = 5;

// Sticky edges need their own background or the row underneath scrolls through
// them; the hover shade has to be repeated for the same reason.
const STICKY_LEFT = 'sticky left-0 z-10 bg-white group-hover:bg-[#F7F8FA]';
const STICKY_RIGHT = 'sticky right-0 z-10 bg-white group-hover:bg-[#F7F8FA]';
const STICKY_HEAD_LEFT = 'sticky left-0 z-20 bg-[#062B1A]';
const STICKY_HEAD_RIGHT = 'sticky right-0 z-20 bg-[#062B1A]';

const EMPTY_SUMMARY = {
  total: 0,
  in_person: 0,
  online: 0,
  undecided: 0,
  attended: 0,
  no_show: 0,
  cancelled: 0,
};

function SignupStatCard({ label, value, icon: Icon, tone, accent = false }) {
  return (
    <Card className={cn(
      'border border-[rgba(6,43,26,0.08)] shadow-sm',
      accent && 'gold-border-left',
    )}>
      {/* Two-up and tighter on a phone. At full width each of these is ~250px
          tall, so a row of them becomes a wall to scroll past before reaching
          the records they describe — seven of them on the signups list. */}
      <CardContent className="p-3 sm:p-4">
        <div className="mb-2 flex items-center justify-between gap-2 sm:mb-3 sm:gap-3">
          <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#062B1A]/50 sm:text-xs sm:tracking-[0.1em]">
            {label}
          </span>
          <span className={cn('rounded-full p-1 sm:p-1.5', tone)}>
            <Icon className="size-3.5 sm:size-4" />
          </span>
        </div>
        <p
          className="text-2xl font-bold text-[#062B1A] sm:text-3xl"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          {value}
        </p>
      </CardContent>
    </Card>
  );
}

export default function AdminWebsiteSignups() {
  const navigate = useNavigate();
  const { loading: authLoading } = useAuth();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [eventGroups, setEventGroups] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [permissionHints, setPermissionHints] = useState({});
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(true);

  const [statusFilter, setStatusFilter] = useState(ALL);
  const [attendanceFilter, setAttendanceFilter] = useState(ALL);
  const [eventFilter, setEventFilter] = useState(ALL);
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0); // 0-based

  // Selection is a list of ids, never "everything matching the filter" — the
  // bulk action deletes exactly what is ticked here and nothing else.
  const [selectedIds, setSelectedIds] = useState([]);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const canCancel = permissions['registrations.cancel'] !== false;
  const canDelete = permissions['registrations.delete'] === true;

  // Debounce the search box -> q (which drives the query).
  useEffect(() => {
    const id = window.setTimeout(() => setQ(searchInput.trim()), 350);
    return () => window.clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    setPage(0);
  }, [statusFilter, attendanceFilter, eventFilter, q]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = { skip: page * PAGE_SIZE, limit: PAGE_SIZE };
      if (statusFilter !== ALL) params.status = statusFilter;
      if (attendanceFilter !== ALL) params.attendance_preference = attendanceFilter;
      if (eventFilter !== ALL) params.event_uuid = eventFilter;
      if (q) params.q = q;
      const { data } = await getWebsiteSignups(params);
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotal(Number(data?.total) || 0);
      setEventGroups(Array.isArray(data?.event_groups) ? data.event_groups : []);
      setPermissions(data?.permissions || {});
      setPermissionHints(data?.permission_hints || {});
      setSummary({ ...EMPTY_SUMMARY, ...(data?.summary || {}) });
      return data;
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Could not load website signups');
      setItems([]);
      setTotal(0);
      setSummary(EMPTY_SUMMARY);
      return null;
    } finally {
      setLoading(false);
    }
  }, [statusFilter, attendanceFilter, eventFilter, q, page]);

  useEffect(() => {
    if (authLoading) return;
    load();
  }, [authLoading, load]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = Math.min(total, page * PAGE_SIZE + items.length);
  const hasActiveFilters =
    statusFilter !== ALL || attendanceFilter !== ALL || eventFilter !== ALL || !!q;

  const headerCount = useMemo(() => {
    if (loading) return 'Loading…';
    return `${rangeStart}–${rangeEnd} of ${total} signup${total === 1 ? '' : 's'}`;
  }, [loading, rangeStart, rangeEnd, total]);

  const clearFilters = () => {
    setStatusFilter(ALL);
    setAttendanceFilter(ALL);
    setEventFilter(ALL);
    setSearchInput('');
    setQ('');
  };

  const openSignup = (signup) => navigate(`/admin/website-signups/${signup.id}`);

  const selected = useMemo(
    () => items.filter((item) => selectedIds.includes(item.id)),
    [items, selectedIds],
  );
  const visibleIds = items.map((item) => item.id);
  const allVisibleSelected = visibleIds.length > 0
    && visibleIds.every((id) => selectedIds.includes(id));

  const toggleRow = (id) => setSelectedIds((current) => (
    current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
  ));
  const selectVisible = () => setSelectedIds((current) => (
    [...new Set([...current, ...visibleIds])]
  ));
  const clearSelection = () => setSelectedIds([]);

  const afterMutation = async (removedIds = []) => {
    if (removedIds.length) {
      setSelectedIds((current) => current.filter((id) => !removedIds.includes(id)));
    }
    const remaining = total - removedIds.length;
    // A page that just emptied itself should step back rather than show nothing.
    if (page > 0 && removedIds.length && remaining <= page * PAGE_SIZE) {
      setPage((current) => Math.max(0, current - 1));
      return;
    }
    await load();
  };

  const runCancel = async ({ reason }) => {
    const target = cancelTarget;
    if (!target) return;
    try {
      setBusy(true);
      const { data } = await cancelWebsiteSignup(target.id, { reason });
      toast.success(data?.already_cancelled
        ? 'That registration was already cancelled'
        : `${target.registration_id || 'Registration'} cancelled`);
      setCancelTarget(null);
      await afterMutation();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Could not cancel this registration');
    } finally {
      setBusy(false);
    }
  };

  const runDelete = async (payload) => {
    const target = deleteTarget;
    if (!target) return;
    try {
      setBusy(true);
      await deleteWebsiteSignup(target.id, payload);
      toast.success(`${target.registration_id || 'Registration'} permanently deleted`);
      setDeleteTarget(null);
      await afterMutation([target.id]);
    } catch (error) {
      console.error(error);
      const detail = error?.response?.data?.detail;
      // The row stays on screen: nothing was removed, so nothing should vanish.
      toast.error(typeof detail === 'string' ? detail : 'Could not delete this registration');
    } finally {
      setBusy(false);
    }
  };

  const runBulkDelete = async ({ reason }) => {
    const ids = selected.map((item) => item.id);
    if (!ids.length) return;
    try {
      setBusy(true);
      const { data } = await bulkDeleteWebsiteSignups({ ids, reason, confirm: true });
      const deleted = Number(data?.deleted) || 0;
      const skipped = Number(data?.skipped) || 0;
      if (deleted) toast.success(`${deleted} registration${deleted === 1 ? '' : 's'} deleted`);
      if (skipped) toast.error(`${skipped} could not be deleted`);
      setBulkOpen(false);
      const removed = (data?.results || [])
        .filter((result) => result?.deleted)
        .map((result) => result.id);
      await afterMutation(removed);
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Could not delete the selected registrations');
    } finally {
      setBusy(false);
    }
  };

  // Header and rows share one grid definition so the columns cannot drift apart.
  const gridTemplateColumns = GRID_TEMPLATE_COLUMNS;
  const deleteHint = permissionHints['registrations.delete']
    || 'Only a full administrator can permanently delete a registration.';

  return (
    <WebsiteEngagementShell
      title="Event Signups"
      description={
        'People who registered for an event on the website. These are leads, not '
        + 'applications — nobody here has a portal account. Click a row to see everything '
        + 'they sent and update it.'
      }
    >

      <section
        aria-label="Website signup summary"
        className="grid grid-cols-2 gap-3 md:grid-cols-2 md:gap-4 xl:grid-cols-7"
        data-testid="signup-summary"
      >
        <SignupStatCard label="Total" value={summary.total} icon={Users} tone="bg-[#062B1A]/5 text-[#062B1A]" />
        <SignupStatCard label="In-Person" value={summary.in_person} icon={MapPin} tone="bg-[#D4AF37]/12 text-[#7A5A05]" accent />
        <SignupStatCard label="Online" value={summary.online} icon={Laptop} tone="bg-sky-50 text-sky-700" />
        <SignupStatCard label="Undecided" value={summary.undecided} icon={CircleHelp} tone="bg-amber-50 text-amber-700" />
        <SignupStatCard label="Attended" value={summary.attended} icon={CheckCircle2} tone="bg-emerald-50 text-emerald-700" />
        <SignupStatCard label="No Shows" value={summary.no_show} icon={UserX} tone="bg-red-50 text-red-700" />
        <SignupStatCard label="Cancelled" value={summary.cancelled} icon={Ban} tone="bg-slate-50 text-slate-600" />
      </section>

      <TableFilterCard>
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="relative max-w-xl flex-1">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[#062B1A]/40" />
            <Input
              type="search"
              aria-label="Search website signups"
              placeholder="Search name, email, phone, agency, or event..."
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              className={cn('pl-9', adminInputClass)}
            />
          </div>
          <div className="text-xs text-[#062B1A]/60">{headerCount}</div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Status reads as pills, the way the Agencies filters do; the two
              lower-traffic dimensions stay as selects to keep the row short. */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-pressed={statusFilter === ALL}
            onClick={() => setStatusFilter(ALL)}
            className={cn(filterPillClass, statusFilter === ALL && filterPillActiveClass)}
          >
            All
          </Button>
          {SIGNUP_STATUSES.map((status) => (
            <Button
              key={status}
              type="button"
              variant="outline"
              size="sm"
              aria-pressed={statusFilter === status}
              onClick={() => setStatusFilter(status)}
              className={cn(filterPillClass, statusFilter === status && filterPillActiveClass)}
            >
              {signupStatusMeta(status).label}
            </Button>
          ))}

          <Select value={attendanceFilter} onValueChange={setAttendanceFilter}>
            <SelectTrigger aria-label="Online/In-Person filter" className={cn('h-8 w-[10.5rem] rounded-full text-xs', adminInputClass)}>
              <SelectValue placeholder="Online/In-Person" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All attendance</SelectItem>
              {ATTENDANCE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* One option per event, keyed by the event UUID. Two generations of
              registration linkage for the same event are one group here, and
              the staff reference code below the name is a secondary label —
              never the thing that does the grouping. */}
          <Select value={eventFilter} onValueChange={setEventFilter}>
            <SelectTrigger aria-label="Event filter" className={cn('h-8 w-[16rem] rounded-full text-xs', adminInputClass)}>
              <SelectValue placeholder="Event" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All events</SelectItem>
              {eventGroups.map((group) => (
                <SelectItem key={group.event_uuid} value={group.event_uuid}>
                  <span className="flex flex-col">
                    <span>{group.label}</span>
                    {group.staff_reference_code && (
                      <span className="font-mono text-[10px] text-[#062B1A]/50">
                        {group.staff_reference_code}
                      </span>
                    )}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {hasActiveFilters && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="h-8 rounded-full px-3 text-xs text-[#062B1A]/65"
            >
              Clear filters
            </Button>
          )}
        </div>
      </TableFilterCard>

      {/* Selection actions live above the table rather than inside a row menu,
          so what is selected and what will happen to it are visible together. */}
      <div className="flex flex-wrap items-center gap-2" data-testid="selection-bar">
        <Button
          type="button" variant="outline" size="sm"
          onClick={selectVisible}
          disabled={loading || items.length === 0 || allVisibleSelected}
          className={filterPillClass}
        >
          Select visible rows
        </Button>
        <Button
          type="button" variant="outline" size="sm"
          onClick={clearSelection}
          disabled={selectedIds.length === 0}
          className={filterPillClass}
        >
          Clear selection
        </Button>
        <span className="text-xs text-[#062B1A]/60" data-testid="selection-count">
          {selectedIds.length} selected
        </span>
        <Button
          type="button" variant="outline" size="sm"
          onClick={() => setBulkOpen(true)}
          disabled={!canDelete || selected.length === 0}
          title={canDelete ? undefined : deleteHint}
          className="h-8 rounded-full border-[rgba(153,27,27,0.3)] px-3 text-xs text-[rgb(153,27,27)]"
        >
          <Trash2 className="mr-1 size-3.5" /> Delete selected
        </Button>
        {!canDelete && (
          <span className="text-xs text-[#062B1A]/45">{deleteHint}</span>
        )}
      </div>

      {/* The full management table will not fit a phone, so it keeps a min-width and
          scrolls inside its own card — the page itself never scrolls sideways.
          The header stays mounted through every refetch: swapping the whole
          table for a skeleton made the columns vanish on each filter click. */}
      {/* Eleven columns — the widest table in the area. Below `md` the same
          registrations are cards, and the row's actions move to the detail
          screen a tap opens: cancelling and deleting a person's record are
          not things to offer under a thumb on a 1,400px-wide scroll. */}
      <DesktopTableOnly>
      <Card className={tableCardClass}>
        <Table style={{ minWidth: `${TABLE_MIN_WIDTH}px` }}>
          <TableHeader>
            <TableRow className={cn('grid', tableHeaderRowClass)} style={{ gridTemplateColumns }}>
              {COLUMNS.map((column) => (
                <TableHead
                  key={column.id}
                  scope="col"
                  className={cn(
                    tableHeadCellClass, 'flex items-center',
                    column.id === 'select' && STICKY_HEAD_LEFT,
                    column.id === 'actions' && STICKY_HEAD_RIGHT,
                  )}
                >
                  {column.id === 'select' ? (
                    <input
                      type="checkbox"
                      aria-label="Select visible rows"
                      checked={allVisibleSelected}
                      onChange={() => (allVisibleSelected ? clearSelection() : selectVisible())}
                      className="size-3.5 accent-[#D4AF37]"
                    />
                  ) : column.label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: SKELETON_ROWS }).map((_, index) => (
                <TableRow
                  key={`skeleton-${index}`}
                  className={cn('grid', tableRowClass)}
                  style={{ gridTemplateColumns }}
                >
                  {COLUMNS.map((column) => (
                    <TableCell key={column.id} className={cn(tableCellClass, ROW_PADDING)}>
                      <div className="h-4 animate-pulse rounded bg-[#F1F3F5]" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : items.length === 0 ? (
              <TableEmptyRow colSpan={COLUMNS.length}>
                {hasActiveFilters
                  ? 'No signups match these filters.'
                  : 'No signups yet. Registrations from the website will appear here.'}
              </TableEmptyRow>
            ) : (
                  items.map((signup) => {
                    const meta = signupStatusMeta(signup.status);
                    const cell = cn(tableCellClass, ROW_PADDING);
                    const checked = selectedIds.includes(signup.id);
                    return (
                      <TableRow
                        key={signup.id}
                        data-testid="signup-row"
                        onClick={() => openSignup(signup)}
                        className={cn('grid cursor-pointer', tableRowClass)}
                        style={{ gridTemplateColumns }}
                      >
                        <TableCell
                          className={cn(cell, STICKY_LEFT, 'flex items-center')}
                          onClick={(event) => event.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            aria-label={`Select ${fullName(signup) || signup.registration_id}`}
                            checked={checked}
                            onChange={() => toggleRow(signup.id)}
                            className="size-3.5 accent-[#D4AF37]"
                          />
                        </TableCell>
                        <TableCell className={cn(cell, 'font-bold text-[#062B1A]')}>
                          <span className="line-clamp-2 break-words">
                            {displayValue(fullName(signup))}
                          </span>
                        </TableCell>
                        <TableCell className={cn(cell, 'text-[#062B1A]/70')}>
                          <span className="line-clamp-2 break-all">{displayValue(signup.email)}</span>
                        </TableCell>
                        <TableCell className={cn(cell, 'text-[#062B1A]/70')}>
                          {displayValue(signup.phone_number)}
                        </TableCell>
                        <TableCell className={cn(cell, 'text-[#062B1A]/70')}>
                          {attendanceLabel(signup.attendance_preference)}
                        </TableCell>
                        <TableCell className={cn(cell, 'text-[#062B1A]/70')}>
                          <span className="line-clamp-2">
                            {heardAboutLabel(
                              signup.how_did_you_hear_about_us,
                              signup.how_did_you_hear_about_us_detail,
                            )}
                          </span>
                        </TableCell>
                        <TableCell className={cn(cell, 'text-[#062B1A]/70')}>
                          <span className="line-clamp-2">{displayValue(signup.agency_name)}</span>
                        </TableCell>
                        <TableCell className={cn(cell, 'text-[#062B1A]/70')}>
                          <span className="line-clamp-2" title={sourcePageLabel(signup.source_page)}>
                            {displayValue(signup.event_title || signup.event_id)}
                          </span>
                        </TableCell>
                        <TableCell className={cell}>
                          <StatusPill tone={meta.pillTone}>{meta.label}</StatusPill>
                        </TableCell>
                        <TableCell className={cn(cell, 'text-[#062B1A]/60')}>
                          {formatDate(signup.created_at)}
                        </TableCell>
                        <TableCell
                          className={cn(cell, STICKY_RIGHT, 'flex items-center gap-1')}
                          onClick={(event) => event.stopPropagation()}
                        >
                          <Button
                            type="button" size="icon" variant="ghost"
                            aria-label={`View ${signup.registration_id}`}
                            onClick={() => openSignup(signup)}
                            className="h-7 w-7 text-[#062B1A]"
                          >
                            <Eye className="size-3.5" />
                          </Button>
                          <Button
                            type="button" size="icon" variant="ghost"
                            aria-label={`Cancel ${signup.registration_id}`}
                            disabled={!canCancel}
                            onClick={() => setCancelTarget(signup)}
                            className="h-7 w-7 text-[#8A6508]"
                          >
                            <Ban className="size-3.5" />
                          </Button>
                          <Button
                            type="button" size="icon" variant="ghost"
                            aria-label={`Delete ${signup.registration_id}`}
                            disabled={!canDelete}
                            title={canDelete ? undefined : deleteHint}
                            onClick={() => setDeleteTarget(signup)}
                            className="h-7 w-7 text-[rgb(153,27,27)]"
                          >
                            <Trash2 className="size-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
            )}
          </TableBody>
        </Table>
      </Card>
      </DesktopTableOnly>

      <MobileCardList data-testid="signup-card-list">
        {loading ? (
          Array.from({ length: SKELETON_ROWS }).map((_, index) => (
            <div
              key={`card-skeleton-${index}`}
              data-testid="signup-skeleton-card"
              className="h-28 animate-pulse rounded-xl border border-[rgba(6,43,26,0.08)] bg-white"
            />
          ))
        ) : items.length === 0 ? (
          <MobileCardNotice>
            {hasActiveFilters
              ? 'No signups match these filters.'
              : 'No website signups yet.'}
          </MobileCardNotice>
        ) : (
          items.map((signup) => {
            const meta = signupStatusMeta(signup.status);
            return (
              <MobileCard
                key={signup.id}
                data-testid="signup-card"
                onClick={() => openSignup(signup)}
              >
                <MobileCardHeader
                  title={fullName(signup) || displayValue(signup.email)}
                  meta={displayValue(signup.email)}
                  badge={<StatusPill tone={meta.pillTone}>{meta.label}</StatusPill>}
                />
                <MobileCardFields>
                  <MobileCardField label="Event">
                    {displayValue(signup.event_title)}
                  </MobileCardField>
                  <MobileCardField label="Attending">
                    {attendanceLabel(signup.attendance_preference)}
                  </MobileCardField>
                  <MobileCardField label="Phone">
                    {displayValue(signup.phone_number)}
                  </MobileCardField>
                  <MobileCardField label="Submitted">
                    {formatDate(signup.created_at)}
                  </MobileCardField>
                </MobileCardFields>
              </MobileCard>
            );
          })
        )}
      </MobileCardList>

      <div className="flex items-center justify-between">
        <p className="text-xs text-[#062B1A]/60">
          Page {page + 1} of {pageCount}
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 0 || loading}
            onClick={() => setPage((current) => Math.max(0, current - 1))}
            className="border-[rgba(6,43,26,0.16)] text-[#062B1A]"
          >
            <ChevronLeft className="mr-1 h-4 w-4" /> Prev
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={page + 1 >= pageCount || loading}
            onClick={() => setPage((current) => current + 1)}
            className="border-[rgba(6,43,26,0.16)] text-[#062B1A]"
          >
            Next <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </div>

      <CancelRegistrationDialog
        open={!!cancelTarget}
        registration={cancelTarget}
        busy={busy}
        onCancel={() => setCancelTarget(null)}
        onConfirm={runCancel}
      />
      <DeleteRegistrationDialog
        open={!!deleteTarget}
        registration={deleteTarget}
        eventReference={
          (eventGroups.find((group) => group.event_uuid === deleteTarget?.event_uuid) || {})
            .staff_reference_code
        }
        busy={busy}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={runDelete}
      />
      <BulkDeleteRegistrationsDialog
        open={bulkOpen}
        registrations={selected}
        busy={busy}
        onCancel={() => setBulkOpen(false)}
        onConfirm={runBulkDelete}
      />
    </WebsiteEngagementShell>
  );
}
